import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useSearchParams, useNavigate } from "react-router-dom";
import { API, formatErr, useAuth } from "@/context/AuthContext";
import { useSites } from "@/context/SiteContext";
import { can } from "@/lib/permissions";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { toast } from "sonner";
import {
  UploadSimple, ImageSquare, FloppyDisk, Buildings, Sparkle, Check, Warning,
  FileText, Truck, IdentificationCard, MagnifyingGlass, Plus, CheckCircle,
  FilePdf,
} from "@phosphor-icons/react";
import SupplierQuickCreate from "@/components/SupplierQuickCreate";
import CategorySelect from "@/components/CategorySelect";
import { enqueueMaterial } from "@/lib/offlineQueue";
import { useOfflineStatus } from "@/hooks/useOfflineStatus";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { titleCaseTR, codeUpperTR, plateUpperTR } from "@/lib/textFormat";

const UNITS = ["adet", "kg", "ton", "m", "m²", "m³", "lt", "torba", "paket", "palet"];

const emptyForm = {
  site_id: "", site_name: "",
  // Düzenleyen firma (issuer)
  supplier_id: "", supplier_name: "",
  // Düzenlenen firma (recipient / alıcı)
  recipient_supplier_id: "", recipient_supplier_name: "",
  material_name: "", quantity: "", unit: "adet",
  category: "",
  delivery_date: new Date().toISOString().slice(0, 10),
  site_location: "", unit_price: "", delivery_note_no: "",
  // Taşıyıcı
  carrier_name: "", carrier_plate: "", carrier_person_id: "",
  carrier_company_name: "", carrier_company_tax_number: "", carrier_company_phone: "",
  photo_path: "", notes: "",
};

// Field label helper with AI badge and confidence dot
const FieldLabel = ({ children, aiKey, confidence, icon: Icon }) => {
  const c = confidence?.[aiKey];
  const color = c === "high" ? "bg-emerald-500" : c === "medium" ? "bg-amber-500" : c === "low" ? "bg-red-500" : null;
  return (
    <Label className="text-xs uppercase tracking-widest font-bold text-slate-700 flex items-center gap-1.5">
      {Icon && <Icon size={12} weight="fill" className="text-[#1B4776]" />}
      {children}
      {color && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#F7FBE9] text-[#4E7A0F] text-[9px] font-bold uppercase tracking-widest">
          <Sparkle size={8} weight="fill" />
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${color}`} />
          AI
        </span>
      )}
    </Label>
  );
};

// Section header
const Section = ({ icon: Icon, title, subtitle, children, testid }) => (
  <div className="border-t border-slate-200 pt-5 mt-5 first:border-0 first:pt-0 first:mt-0" data-testid={testid}>
    <div className="flex items-center gap-2 mb-4">
      <div className="w-8 h-8 bg-[#1B4776] flex items-center justify-center">
        <Icon size={16} weight="duotone" className="text-[#95C11F]" />
      </div>
      <div>
        <div className="text-sm font-bold text-slate-900 font-heading">{title}</div>
        {subtitle && <div className="text-[10px] uppercase tracking-widest text-slate-500">{subtitle}</div>}
      </div>
    </div>
    {children}
  </div>
);

/**
 * SupplierField — Combobox-benzeri firma alanı.
 * - Serbest metin girişi (name)
 * - "Sistemden Seç" popover ile mevcut firma seçimi
 * - Eşleşmiş / Yeni / Boş durum rozetleri + "Sisteme Kaydet" kısayolu
 * props:
 *   name (string), onNameChange
 *   supplierId (string), onSupplierChange({id, name}|null)
 *   suppliers (list)
 *   confidenceKey ("issuer"|"recipient"), confidence
 *   onRegisterClick() — bilinmeyen firmayı sisteme kaydet paneli açar
 *   placeholder, testidPrefix
 */
const SupplierField = ({
  name, onNameChange, supplierId, onSupplierChange, suppliers,
  confidenceKey, confidence, onRegisterClick, placeholder, testidPrefix, offline,
}) => {
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const matched = supplierId ? suppliers.find(s => s.id === supplierId) : null;
  const hasName = (name || "").trim().length > 0;

  return (
    <div>
      <FieldLabel aiKey={confidenceKey} confidence={confidence}>Firma Adı</FieldLabel>
      <div className="mt-2 flex gap-2">
        <Input
          value={name}
          data-testid={`${testidPrefix}-name`}
          onChange={(e) => {
            onNameChange(e.target.value);
            // İsim değişirse eşleşme kırılır
            if (supplierId) onSupplierChange(null);
          }}
          placeholder={placeholder || "Firma adını yazın veya arayın"}
          className="rounded-none sm:rounded-sm flex-1"
        />
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button" variant="outline"
              data-testid={`${testidPrefix}-picker`}
              className="rounded-none sm:rounded-sm border-slate-300 gap-1.5">
              <MagnifyingGlass size={14} weight="bold" />
              <span className="hidden sm:inline">Sistemden Seç</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="p-0 w-80 rounded-none sm:rounded-sm">
            <Command>
              <CommandInput placeholder="Firma ara..." />
              <CommandList>
                <CommandEmpty>Firma bulunamadı.</CommandEmpty>
                <CommandGroup>
                  {suppliers.map(s => (
                    <CommandItem
                      key={s.id}
                      value={`${s.name} ${s.title || ""} ${s.tax_number || ""}`}
                      onSelect={() => {
                        onSupplierChange({ id: s.id, name: s.name });
                        onNameChange(s.name);
                        setPickerOpen(false);
                      }}
                      data-testid={`${testidPrefix}-picker-item-${s.id}`}
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">{s.name}</span>
                        {s.title && s.title !== s.name && (
                          <span className="text-[10px] text-slate-500">{s.title}</span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Status */}
      <div className="mt-2 min-h-[22px]">
        {matched ? (
          <div className="flex items-center gap-1.5 text-xs text-emerald-700">
            <CheckCircle size={13} weight="fill" />
            <span>Sistemde kayıtlı: <strong>{matched.title || matched.name}</strong></span>
          </div>
        ) : hasName ? (
          <div className="flex items-center justify-between gap-2 text-xs">
            <div className="flex items-center gap-1.5 text-amber-700">
              <Warning size={13} weight="fill" />
              <span>Bu firma sistemde kayıtlı değil</span>
            </div>
            <button
              type="button"
              onClick={onRegisterClick}
              disabled={offline}
              data-testid={`${testidPrefix}-register`}
              title={offline ? "Çevrimdışı — sinyal gelince sisteme kaydedebilirsiniz. Şimdilik serbest metin olarak kayıt kuyruğa alınır." : ""}
              className="inline-flex items-center gap-1 text-[#1B4776] hover:underline font-semibold uppercase text-[10px] tracking-widest disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline"
            >
              <Plus size={11} weight="bold" /> Sisteme Kaydet
            </button>
          </div>
        ) : (
          <div className="text-[10px] text-slate-400">
            Serbest yazabilir veya listeden seçebilirsiniz.
          </div>
        )}
      </div>
    </div>
  );
};

export default function MaterialEntry() {
  const { user } = useAuth();
  const { sites, activeSiteId } = useSites();
  const { online } = useOfflineStatus();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const editId = searchParams.get("edit") || "";
  const isEditMode = !!editId;
  const [loadingRecord, setLoadingRecord] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [confidence, setConfidence] = useState({}); // AI onay durumu
  const [aiActive, setAiActive] = useState(false);
  const [aiData, setAiData] = useState(null);
  // Queue of unmatched suppliers to prompt (issuer first, then recipient)
  // items: { role: "issuer"|"recipient", info: {name,title,...} }
  const [pendingQueue, setPendingQueue] = useState([]);
  const [currentPending, setCurrentPending] = useState(null);
  const [supplierSheetOpen, setSupplierSheetOpen] = useState(false);
  const [supplierSaving, setSupplierSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoFile, setPhotoFile] = useState(null); // offline queue için Blob referansı
  const [isPdfPreview, setIsPdfPreview] = useState(false); // önizleme PDF mi?
  // AI ile tespit edilen çoklu kalemler ({selected, material_name, quantity, unit, unit_price})
  const [multiItems, setMultiItems] = useState([]);
  const [bulkSaving, setBulkSaving] = useState(false);
  // Ref-guard: React state async olarak güncellenir; ard arda çok hızlı
  // tetiklenen submit çağrılarını engellemek için senkron kilit.
  const submitLockRef = useRef(false);
  const bulkLockRef = useRef(false);

  useEffect(() => {
    axios.get(`${API}/suppliers`).then(r => setSuppliers(r.data)).catch(() => {});
  }, []);

  // Feb 2026: İrsaliye giriş formunda sözleşme sorulmaz.
  // Kullanıcı, irsaliye listesinde ContractLinkChip üzerinden istediği sözleşmeyi
  // (tedarikçi bakılmaksızın, aynı şantiyede) manuel olarak eşleştirir.

  // Aktif şantiye seçili ise otomatik doldur (edit modunda ATLA — kaydın kendi şantiyesi kullanılır)
  useEffect(() => {
    if (isEditMode) return;
    if (activeSiteId && activeSiteId !== "all") {
      const s = sites.find(x => x.id === activeSiteId);
      if (s) setForm(f => ({ ...f, site_id: s.id, site_name: s.name }));
    }
  }, [activeSiteId, sites, isEditMode]);

  // Edit modu — mevcut kaydı yükle
  useEffect(() => {
    if (!editId) return;
    let alive = true;
    (async () => {
      setLoadingRecord(true);
      try {
        const { data } = await axios.get(`${API}/materials/${editId}`);
        if (!alive) return;
        setForm({
          site_id: data.site_id || "",
          site_name: data.site_name || "",
          supplier_id: data.supplier_id || "",
          supplier_name: data.supplier_name || "",
          recipient_supplier_id: data.recipient_supplier_id || "",
          recipient_supplier_name: data.recipient_supplier_name || "",
          material_name: data.material_name || "",
          quantity: data.quantity != null ? String(data.quantity) : "",
          unit: data.unit || "adet",
          category: data.category || "",
          delivery_date: data.delivery_date || new Date().toISOString().slice(0, 10),
          site_location: data.site_location || "",
          unit_price: data.unit_price != null ? String(data.unit_price) : "",
          delivery_note_no: data.delivery_note_no || "",
          carrier_name: data.carrier_name || "",
          carrier_plate: data.carrier_plate || "",
          carrier_person_id: data.carrier_person_id || "",
          carrier_company_name: data.carrier_company_name || "",
          carrier_company_tax_number: data.carrier_company_tax_number || "",
          carrier_company_phone: data.carrier_company_phone || "",
          photo_path: data.photo_path || "",
          notes: data.notes || "",
        });
      } catch (err) {
        toast.error(formatErr(err));
        navigate("/irsaliye-arsivi");
      } finally {
        setLoadingRecord(false);
      }
    })();
    return () => { alive = false; };
  }, [editId, navigate]);

  // Yeni bir pending çıktığında dialogu aç
  useEffect(() => {
    if (!currentPending && pendingQueue.length > 0) {
      const [next, ...rest] = pendingQueue;
      setCurrentPending(next);
      setPendingQueue(rest);
    }
  }, [pendingQueue, currentPending]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  if (!can.writeMaterial(user)) {
    return (
      <Shell title="Malzeme Girişi" subtitle="Yetki Gerekli">
        <Card className="p-8 rounded-none sm:rounded-sm text-center">
          <div className="text-slate-600">Bu ekrana erişim yetkiniz bulunmuyor.</div>
        </Card>
      </Shell>
    );
  }

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoUrl(URL.createObjectURL(file));
    if (!navigator.onLine) {
      toast.message("Çevrimdışısınız — fotoğraf cihaza kaydedildi, sinyal gelince gönderilecek");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("photo_path", data.path);
      setConfidence({});
      toast.success("Fotoğraf yüklendi");
    } catch (err) {
      // Offline / ağ hatası → yerelde tut, kuyruk gönderirken tekrar dene
      toast.message("Fotoğraf sıraya alındı (offline)");
    } finally {
      setUploading(false);
    }
  };

  const onAiScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const asPdf = /\.pdf$/i.test(file.name) || file.type === "application/pdf";
    setIsPdfPreview(asPdf);
    setPhotoFile(file);
    setAiActive(true);
    if (!asPdf) setPhotoUrl(URL.createObjectURL(file));
    else setPhotoUrl("");
    try {
      const uploadFd = new FormData();
      uploadFd.append("file", file);
      const uploadPromise = axios
        .post(`${API}/upload`, uploadFd, { headers: { "Content-Type": "multipart/form-data" } })
        .catch(() => null);

      const aiFd = new FormData();
      aiFd.append("file", file);
      const { data } = await axios.post(`${API}/materials/parse-invoice`, aiFd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const issuer = data.issuer || {};
      const recipient = data.recipient || {};
      const carrier = data.carrier || {};

      // Formu doldur
      setForm((f) => ({
        ...f,
        supplier_id: data.matched_issuer_id || "",
        supplier_name: data.matched_issuer_name || issuer.name || issuer.title || "",
        recipient_supplier_id: data.matched_recipient_id || "",
        recipient_supplier_name: data.matched_recipient_name || recipient.name || recipient.title || "",
        material_name: data.material_name || f.material_name,
        quantity: data.quantity != null ? String(data.quantity) : f.quantity,
        unit: data.unit && UNITS.includes(data.unit) ? data.unit : f.unit,
        unit_price: data.unit_price != null ? String(data.unit_price) : f.unit_price,
        delivery_date: data.delivery_date || f.delivery_date,
        delivery_note_no: data.delivery_note_no || f.delivery_note_no,
        carrier_name: carrier.person_name || f.carrier_name,
        carrier_plate: carrier.plate || f.carrier_plate,
        carrier_person_id: carrier.person_id || f.carrier_person_id,
        carrier_company_name: carrier.company_name || f.carrier_company_name,
        carrier_company_tax_number: carrier.company_tax_number || f.carrier_company_tax_number,
        carrier_company_phone: carrier.company_phone || f.carrier_company_phone,
      }));
      setConfidence(data.confidence || {});
      setAiData(data);

      // Çoklu kalem tespiti
      const items = Array.isArray(data.items) ? data.items.filter(i => i && i.material_name) : [];
      if (items.length > 1) {
        setMultiItems(items.map((it) => ({
          selected: true,
          material_name: it.material_name || "",
          quantity: it.quantity != null ? String(it.quantity) : "",
          unit: it.unit && UNITS.includes(it.unit) ? it.unit : "adet",
          unit_price: it.unit_price != null ? String(it.unit_price) : "",
        })));
      } else {
        setMultiItems([]);
      }

      const up = await uploadPromise;
      if (up?.data?.path) set("photo_path", up.data.path);

      // Unmatched firmaları kuyruğa al
      const queue = [];
      const issuerHasInfo = issuer.name || issuer.title || issuer.tax_number;
      const recipientHasInfo = recipient.name || recipient.title || recipient.tax_number;
      if (!data.matched_issuer_id && issuerHasInfo) {
        queue.push({ role: "issuer", info: issuer });
      }
      if (!data.matched_recipient_id && recipientHasInfo) {
        queue.push({ role: "recipient", info: recipient });
      }
      if (queue.length > 0) {
        setPendingQueue(queue);
      } else {
        toast.success("İrsaliye başarıyla okundu — bilgileri kontrol edip kaydedin");
      }
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setAiActive(false);
      e.target.value = "";
    }
  };

  // Sheet kapandıktan / firma kaydedildikten sonra doğru role'e ata
  const saveSupplierFromAi = async (payload) => {
    setSupplierSaving(true);
    try {
      const { data } = await axios.post(`${API}/suppliers`, payload);
      // Listeye ekle / güncelle
      setSuppliers((prev) => {
        const withoutDup = prev.filter((s) => s.id !== data.id);
        return [...withoutDup, data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr"));
      });
      // Radix Select sonraki render'da item'i tanısın diye setTimeout
      const roleKey = currentPending?.role === "recipient"
        ? { id: "recipient_supplier_id", name: "recipient_supplier_name" }
        : { id: "supplier_id", name: "supplier_name" };
      setTimeout(() => {
        setForm((f) => ({ ...f, [roleKey.id]: data.id, [roleKey.name]: data.name }));
      }, 0);
      toast.success(`${currentPending?.role === "recipient" ? "Düzenlenen" : "Düzenleyen"} firma kaydedildi ve seçildi`);
      setSupplierSheetOpen(false);
      setCurrentPending(null);
      return data;
    } catch (e) {
      toast.error(formatErr(e));
      return null;
    } finally { setSupplierSaving(false); }
  };

  const isOfflineError = (err) =>
    !navigator.onLine ||
    err?.code === "ERR_NETWORK" ||
    err?.message === "Network Error" ||
    !err?.response;

  const submit = async (e) => {
    e.preventDefault();
    if (submitLockRef.current) return;  // senkron kilit: çift-tık koruması
    if (!form.site_id) { toast.error("Lütfen şantiye seçin"); return; }
    if (!(form.supplier_name || "").trim()) { toast.error("Lütfen düzenleyen firma adını yazın"); return; }
    submitLockRef.current = true;
    setSaving(true);
    const payload = {
      ...form,
      supplier_name: form.supplier_name.trim(),
      recipient_supplier_name: (form.recipient_supplier_name || "").trim(),
      material_name: titleCaseTR((form.material_name || "").trim()),
      delivery_note_no: codeUpperTR(form.delivery_note_no),
      site_location: titleCaseTR(form.site_location),
      carrier_name: titleCaseTR(form.carrier_name),
      carrier_plate: plateUpperTR(form.carrier_plate),
      carrier_company_name: titleCaseTR(form.carrier_company_name),
      notes: titleCaseTR(form.notes),
      quantity: parseFloat(form.quantity) || 0,
      unit_price: parseFloat(form.unit_price) || 0,
    };
    // Feb 2026: Sözleşme bağlantısı giriş formundan kaldırıldı.
    // Kaydedilen malzeme, listedeki ContractLinkChip üzerinden manuel olarak eşleştirilir.
    const resetForm = () => {
      const activeSite = activeSiteId && activeSiteId !== "all"
        ? sites.find(x => x.id === activeSiteId) : null;
      setForm({
        ...emptyForm,
        site_id: activeSite?.id || "",
        site_name: activeSite?.name || "",
      });
      setPhotoUrl(""); setPhotoFile(null); setIsPdfPreview(false); setConfidence({}); setAiData(null);
    };

    try {
      if (!navigator.onLine) throw new Error("offline-preflight");
      if (isEditMode) {
        await axios.put(`${API}/materials/${editId}`, payload);
        toast.success("Kayıt güncellendi");
        navigate("/irsaliye-arsivi");
      } else {
        await axios.post(`${API}/materials`, payload);
        toast.success("Malzeme kaydedildi");
        resetForm();
      }
    } catch (err) {
      if (err?.message === "offline-preflight" || isOfflineError(err)) {
        if (isEditMode) {
          toast.error("Kayıt güncellemek için internet bağlantısı gerekli");
        } else {
          try {
            const n = await enqueueMaterial({
              kind: "material",
              payload,
              photoFile: !payload.photo_path ? photoFile : null,
            });
            toast.success(`Çevrimdışı — kuyruğa alındı (${n} kayıt bekliyor)`);
            resetForm();
          } catch (e2) {
            toast.error("Kayıt yerelde saklanamadı");
          }
        }
      } else {
        toast.error(formatErr(err));
      }
    } finally {
      setSaving(false);
      // Kilidi kısa gecikmeyle bırak (tıklama sonrası hemen ikinci event
      // gelirse yine yakalasın).
      setTimeout(() => { submitLockRef.current = false; }, 500);
    }
  };

  const bulkSubmit = async () => {
    if (bulkLockRef.current) return;  // senkron kilit
    if (!form.site_id) { toast.error("Lütfen şantiye seçin"); return; }
    if (!(form.supplier_name || "").trim()) { toast.error("Lütfen düzenleyen firma adını yazın"); return; }
    const chosen = multiItems.filter(x => x.selected && x.material_name && parseFloat(x.quantity) > 0);
    if (chosen.length === 0) { toast.error("En az bir kalem seçin"); return; }
    bulkLockRef.current = true;
    setBulkSaving(true);
    const payload = {
      site_id: form.site_id, site_name: form.site_name,
      supplier_id: form.supplier_id, supplier_name: form.supplier_name.trim(),
      recipient_supplier_id: form.recipient_supplier_id,
      recipient_supplier_name: (form.recipient_supplier_name || "").trim(),
      delivery_date: form.delivery_date, delivery_note_no: codeUpperTR(form.delivery_note_no),
      site_location: titleCaseTR(form.site_location),
      carrier_name: titleCaseTR(form.carrier_name), carrier_plate: plateUpperTR(form.carrier_plate),
      carrier_person_id: form.carrier_person_id,
      carrier_company_name: titleCaseTR(form.carrier_company_name),
      carrier_company_tax_number: form.carrier_company_tax_number,
      carrier_company_phone: form.carrier_company_phone,
      photo_path: form.photo_path, notes: titleCaseTR(form.notes),
      items: chosen.map(x => ({
        material_name: titleCaseTR((x.material_name || "").trim()),
        quantity: parseFloat(x.quantity) || 0,
        unit: x.unit || "adet",
        unit_price: parseFloat(x.unit_price) || 0,
      })),
    };
    const resetForm = () => {
      const activeSite = activeSiteId && activeSiteId !== "all"
        ? sites.find(x => x.id === activeSiteId) : null;
      setForm({ ...emptyForm, site_id: activeSite?.id || "", site_name: activeSite?.name || "" });
      setPhotoUrl(""); setPhotoFile(null); setIsPdfPreview(false); setConfidence({}); setAiData(null); setMultiItems([]);
    };

    try {
      if (!navigator.onLine) throw new Error("offline-preflight");
      const { data } = await axios.post(`${API}/materials/bulk`, payload);
      toast.success(`${data.count} kalem başarıyla kaydedildi`);
      resetForm();
    } catch (e) {
      if (e?.message === "offline-preflight" || isOfflineError(e)) {
        try {
          const n = await enqueueMaterial({
            kind: "bulk",
            payload,
            photoFile: !payload.photo_path ? photoFile : null,
          });
          toast.success(`Çevrimdışı — ${chosen.length} kalem kuyruğa alındı (${n} paket bekliyor)`);
          resetForm();
        } catch (e2) {
          toast.error("Kayıt yerelde saklanamadı");
        }
      } else {
        toast.error(formatErr(e));
      }
    } finally {
      setBulkSaving(false);
      setTimeout(() => { bulkLockRef.current = false; }, 500);
    }
  };

  const activeSuppliersSorted = [...suppliers].sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr"));

  return (
    <Shell title={isEditMode ? "İrsaliyeyi Düzenle" : "Malzeme Girişi"}
           subtitle={isEditMode ? "Mevcut kaydı güncelle" : undefined}>
      {loadingRecord && (
        <div className="mb-4 p-3 border border-amber-300 bg-amber-50 text-amber-800 text-xs font-semibold uppercase tracking-widest">
          Kayıt yükleniyor…
        </div>
      )}
      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Sol / Ana form */}
        <Card className="lg:col-span-2 p-6 rounded-none sm:rounded-sm border-slate-200 shadow-sm">
          {/* Şantiye */}
          <Section icon={Buildings} title="Şantiye" subtitle="Kayıt hangi şantiyeye ait" testid="section-site">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Şantiye</Label>
                <Select
                  value={form.site_id}
                  onValueChange={(v) => {
                    const s = sites.find(x => x.id === v);
                    setForm(f => ({ ...f, site_id: v, site_name: s?.name || "" }));
                  }}
                >
                  <SelectTrigger className="mt-2 rounded-none sm:rounded-sm" data-testid="site-select">
                    <SelectValue placeholder="Şantiye seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.filter(s => s.status === "aktif").map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        <span className="font-mono text-xs text-slate-500 mr-2">{s.code}</span>
                        {s.name}
                      </SelectItem>
                    ))}
                    {sites.length === 0 && (
                      <div className="px-3 py-2 text-xs text-slate-500">Önce Şantiyeler sayfasından şantiye ekleyin.</div>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Şantiye Konumu</Label>
                <Input data-testid="site-location" value={form.site_location}
                       onChange={e => set("site_location", titleCaseTR(e.target.value))}
                       placeholder="Örn: A Blok, Depo 1"
                       className="mt-2 rounded-none sm:rounded-sm" />
              </div>
            </div>
          </Section>

          {/* Düzenleyen Firma (Issuer) */}
          <Section icon={Buildings} title="Düzenleyen Firma" subtitle="İrsaliyeyi kesen / satıcı taraf" testid="section-issuer">
            <SupplierField
              name={form.supplier_name}
              onNameChange={(v) => set("supplier_name", v)}
              supplierId={form.supplier_id}
              onSupplierChange={(s) => {
                if (!s) { set("supplier_id", ""); return; }
                setForm(f => ({ ...f, supplier_id: s.id, supplier_name: s.name }));
              }}
              suppliers={activeSuppliersSorted}
              confidenceKey="issuer"
              confidence={confidence}
              onRegisterClick={() => {
                setCurrentPending({
                  role: "issuer",
                  info: (aiData && aiData.issuer) || { name: form.supplier_name, title: form.supplier_name },
                });
                setSupplierSheetOpen(true);
              }}
              placeholder="Örn: Titan Çimento A.Ş."
              testidPrefix="issuer"
              offline={!online}
            />
          </Section>

          {/* Düzenlenen Firma (Recipient) */}
          <Section icon={Buildings} title="Düzenlenen Firma" subtitle="İrsaliyenin alıcısı / kesildiği taraf" testid="section-recipient">
            <SupplierField
              name={form.recipient_supplier_name}
              onNameChange={(v) => set("recipient_supplier_name", v)}
              supplierId={form.recipient_supplier_id}
              onSupplierChange={(s) => {
                if (!s) { set("recipient_supplier_id", ""); return; }
                setForm(f => ({ ...f, recipient_supplier_id: s.id, recipient_supplier_name: s.name }));
              }}
              suppliers={activeSuppliersSorted}
              confidenceKey="recipient"
              confidence={confidence}
              onRegisterClick={() => {
                setCurrentPending({
                  role: "recipient",
                  info: (aiData && aiData.recipient) || { name: form.recipient_supplier_name, title: form.recipient_supplier_name },
                });
                setSupplierSheetOpen(true);
              }}
              placeholder="Örn: Kolaçoğlu İnşaat Ltd. Şti."
              testidPrefix="recipient"
              offline={!online}
            />
          </Section>

          {/* İrsaliye Bilgileri */}
          <Section icon={FileText} title="İrsaliye Bilgileri" subtitle="Belgedeki malzeme detayı" testid="section-invoice">            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FieldLabel aiKey="delivery_date" confidence={confidence}>Tarih</FieldLabel>
                <Input
                  type="date" data-testid="delivery-date"
                  value={form.delivery_date}
                  onChange={e => set("delivery_date", e.target.value)}
                  className="mt-2 rounded-none sm:rounded-sm"
                />
              </div>
              <div>
                <FieldLabel aiKey="delivery_note_no" confidence={confidence}>İrsaliye No</FieldLabel>
                <Input data-testid="delivery-note-no" value={form.delivery_note_no}
                       onChange={e => set("delivery_note_no", codeUpperTR(e.target.value))}
                       className="mt-2 rounded-none sm:rounded-sm font-mono" />
              </div>
              <div className="md:col-span-2">
                <FieldLabel aiKey="material_name" confidence={confidence}>Malzeme Adı</FieldLabel>
                <Input data-testid="material-name" required value={form.material_name}
                       onChange={e => set("material_name", titleCaseTR(e.target.value))}
                       placeholder="Örn: Çimento, Demir 12mm, Tuğla..."
                       className="mt-2 rounded-none sm:rounded-sm" />
              </div>
              <div>
                <FieldLabel aiKey="quantity" confidence={confidence}>Miktar</FieldLabel>
                <Input type="number" step="0.01" data-testid="quantity" required value={form.quantity}
                       onChange={e => set("quantity", e.target.value)}
                       className="mt-2 rounded-none sm:rounded-sm" />
              </div>
              <div>
                <FieldLabel aiKey="unit" confidence={confidence}>Birim</FieldLabel>
                <Select value={form.unit} onValueChange={(v) => set("unit", v)}>
                  <SelectTrigger className="mt-2 rounded-none sm:rounded-sm" data-testid="unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel aiKey="unit_price" confidence={confidence}>Birim Fiyat (₺)</FieldLabel>
                <Input type="number" step="0.01" data-testid="unit-price" value={form.unit_price}
                       onChange={e => set("unit_price", e.target.value)}
                       className="mt-2 rounded-none sm:rounded-sm" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Kategori</Label>
                <div className="mt-2">
                  <CategorySelect
                    value={form.category || ""}
                    onChange={(v) => set("category", v)}
                    allowEmpty={true}
                    allOptionLabel="Kategori Yok"
                    placeholder="Malzeme kategorisi seçin"
                    testIdPrefix="mat-category"
                  />
                </div>
              </div>
            </div>
          </Section>

          {/* Feb 2026: Sözleşme bağlantısı bu formdan kaldırıldı.
              Kayıttan sonra irsaliye listesinde ContractLinkChip üzerinden manuel eşleştirilir. */}

          {/* Çoklu kalem (AI tespit) */}
          {multiItems.length > 1 && (
            <div className="mt-5 border border-[#95C11F] bg-[#F7FBE9]/60 p-4" data-testid="multi-items-panel">
              <div className="flex items-center gap-2 mb-3">
                <Sparkle size={16} weight="fill" className="text-[#4E7A0F]" />
                <span className="text-[10px] uppercase tracking-widest font-bold text-[#4E7A0F]">
                  AI Çoklu Kalem Tespiti — {multiItems.length} kalem
                </span>
                <span className="ml-auto text-[10px] text-slate-500">
                  Toplu kaydet ile aynı irsaliye başlığıyla hepsi eklenir
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                      <th className="text-center py-2 w-10">
                        <input type="checkbox"
                               checked={multiItems.every(x => x.selected)}
                               onChange={(e) => {
                                 const v = e.target.checked;
                                 setMultiItems(prev => prev.map(x => ({ ...x, selected: v })));
                               }} />
                      </th>
                      <th className="text-left py-2 font-bold">Malzeme</th>
                      <th className="text-right py-2 font-bold w-24">Miktar</th>
                      <th className="text-left py-2 font-bold w-20">Birim</th>
                      <th className="text-right py-2 font-bold w-32">Birim Fiyat (₺)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {multiItems.map((it, i) => (
                      <tr key={i} className={i % 2 ? "bg-white" : "bg-white/50"} data-testid={`multi-row-${i}`}>
                        <td className="text-center py-1">
                          <input type="checkbox" checked={it.selected}
                                 data-testid={`multi-check-${i}`}
                                 onChange={(e) => {
                                   const v = e.target.checked;
                                   setMultiItems(prev => prev.map((x, j) => j === i ? { ...x, selected: v } : x));
                                 }} />
                        </td>
                        <td className="py-1">
                          <Input value={it.material_name}
                                 data-testid={`multi-mat-${i}`}
                                 onChange={(e) => setMultiItems(prev => prev.map((x, j) => j === i ? { ...x, material_name: e.target.value } : x))}
                                 className="rounded-none sm:rounded-sm h-8 text-sm" />
                        </td>
                        <td className="py-1 pr-1">
                          <Input type="number" step="0.01" value={it.quantity}
                                 onChange={(e) => setMultiItems(prev => prev.map((x, j) => j === i ? { ...x, quantity: e.target.value } : x))}
                                 className="rounded-none sm:rounded-sm h-8 text-sm text-right font-mono" />
                        </td>
                        <td className="py-1 pr-1">
                          <Select value={it.unit} onValueChange={(v) =>
                            setMultiItems(prev => prev.map((x, j) => j === i ? { ...x, unit: v } : x))
                          }>
                            <SelectTrigger className="rounded-none sm:rounded-sm h-8 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="py-1">
                          <Input type="number" step="0.01" value={it.unit_price}
                                 onChange={(e) => setMultiItems(prev => prev.map((x, j) => j === i ? { ...x, unit_price: e.target.value } : x))}
                                 className="rounded-none sm:rounded-sm h-8 text-sm text-right font-mono" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <div className="text-xs text-slate-600">
                  <strong>{multiItems.filter(x => x.selected).length}</strong> / {multiItems.length} kalem seçili
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setMultiItems([])}
                          className="rounded-none sm:rounded-sm border-slate-300 h-9">
                    Çoklu Modu Kapat
                  </Button>
                  <Button type="button" onClick={bulkSubmit} data-testid="bulk-save"
                          disabled={bulkSaving}
                          className="rounded-none sm:rounded-sm bg-[#95C11F] hover:bg-[#7FA518] text-[#1B2A3A] font-bold h-9">
                    <FloppyDisk size={14} weight="bold" className="mr-1" />
                    {bulkSaving ? "Kaydediliyor..." : `Toplu Kaydet (${multiItems.filter(x=>x.selected).length} kalem)`}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Taşıyıcı */}
          <Section icon={Truck} title="Taşıyıcı" subtitle="Nakliye / şoför bilgileri (opsiyonel)" testid="section-carrier">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FieldLabel aiKey="carrier" confidence={confidence} icon={IdentificationCard}>Şoför Ad Soyad</FieldLabel>
                <Input data-testid="carrier-name" value={form.carrier_name}
                       onChange={e => set("carrier_name", titleCaseTR(e.target.value))}
                       className="mt-2 rounded-none sm:rounded-sm" />
              </div>
              <div>
                <FieldLabel aiKey="carrier" confidence={confidence}>Araç Plakası</FieldLabel>
                <Input data-testid="carrier-plate" value={form.carrier_plate}
                       onChange={e => set("carrier_plate", plateUpperTR(e.target.value))}
                       placeholder="34 ABC 123"
                       className="mt-2 rounded-none sm:rounded-sm font-mono uppercase" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Şoför T.C. / Kimlik No</Label>
                <Input data-testid="carrier-person-id" value={form.carrier_person_id}
                       onChange={e => set("carrier_person_id", e.target.value)}
                       className="mt-2 rounded-none sm:rounded-sm font-mono" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Taşıyıcı Firma Adı</Label>
                <Input data-testid="carrier-company-name" value={form.carrier_company_name}
                       onChange={e => set("carrier_company_name", titleCaseTR(e.target.value))}
                       className="mt-2 rounded-none sm:rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Taşıyıcı Firma VKN</Label>
                <Input data-testid="carrier-company-vkn" value={form.carrier_company_tax_number}
                       onChange={e => set("carrier_company_tax_number", e.target.value)}
                       className="mt-2 rounded-none sm:rounded-sm font-mono" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Taşıyıcı Firma Telefon</Label>
                <Input data-testid="carrier-company-phone" value={form.carrier_company_phone}
                       onChange={e => set("carrier_company_phone", e.target.value)}
                       className="mt-2 rounded-none sm:rounded-sm" />
              </div>
            </div>
          </Section>

          {/* Notlar */}
          <Section icon={FileText} title="Notlar" subtitle="Ek açıklama" testid="section-notes">
            <Textarea data-testid="notes" value={form.notes}
                      onChange={e => set("notes", titleCaseTR(e.target.value))}
                      className="rounded-none sm:rounded-sm" rows={3}
                      placeholder="Kayıt ile ilgili ek notlar..." />
          </Section>

          <div className="mt-6 flex gap-2">
            {isEditMode && (
              <Button type="button" variant="outline"
                      data-testid="cancel-edit"
                      onClick={() => navigate("/irsaliye-arsivi")}
                      className="h-11 rounded-none sm:rounded-sm border-slate-300 font-bold tracking-wide">
                İptal
              </Button>
            )}
            <Button type="submit" data-testid="save-material"
                    disabled={saving || loadingRecord}
                    className="flex-1 h-11 rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white font-bold tracking-wide">
              <FloppyDisk size={18} weight="bold" className="mr-2" />
              {saving
                ? (isEditMode ? "Güncelleniyor..." : "Kaydediliyor...")
                : (isEditMode ? "Değişiklikleri Kaydet" : "Kaydı Oluştur")}
            </Button>
          </div>
        </Card>

        {/* Sağ / AI panel */}
        <Card className="p-6 rounded-none sm:rounded-sm border-slate-200 shadow-sm h-fit lg:sticky lg:top-4">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Yapay Zeka Destekli</div>
          <div className="text-lg font-bold text-slate-900 mb-4 font-heading">İrsaliyeyi Tara ve Doldur</div>

          <label className={`block ${online ? "cursor-pointer" : "cursor-not-allowed"}`} data-testid="ai-scan-label">
            <div className={`border-2 border-dashed transition-all p-6 text-center ${
              !online
                ? "border-slate-300 bg-slate-100 text-slate-500"
                : aiActive
                ? "border-[#95C11F] bg-[#F7FBE9]"
                : "border-[#1B4776] bg-gradient-to-br from-[#1B4776] to-[#153859] text-white hover:opacity-95"
            }`}>
              {!online ? (
                <>
                  <div className="w-12 h-12 mx-auto flex items-center justify-center mb-3 bg-slate-300">
                    <Sparkle size={22} weight="fill" className="text-slate-600" />
                  </div>
                  <div className="text-sm font-bold text-slate-700">Çevrimdışı — AI kullanılamaz</div>
                  <div className="text-xs mt-1 text-slate-500">
                    Sinyal geldiğinde tekrar deneyebilirsiniz. Şimdilik formu elle doldurun; giriş sıraya alınıp otomatik gönderilir.
                  </div>
                </>
              ) : photoUrl && !aiActive ? (
                <img src={photoUrl} alt="Yüklenen" className="max-h-40 mx-auto" />
              ) : isPdfPreview && !aiActive ? (
                <div className="py-6">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center mb-3 bg-rose-100 border border-rose-300">
                    <FilePdf size={26} weight="bold" className="text-rose-700" />
                  </div>
                  <div className="text-sm font-bold text-slate-700">PDF taraması hazırlandı</div>
                  <div className="text-xs mt-1 text-slate-500 truncate max-w-[220px] mx-auto">
                    {photoFile?.name}
                  </div>
                </div>
              ) : (
                <>
                  <div className="w-12 h-12 mx-auto flex items-center justify-center mb-3 bg-[#95C11F]">
                    <Sparkle size={22} weight="fill" className="text-[#1B2A3A]" />
                  </div>
                  <div className={`text-sm font-bold ${aiActive ? "text-[#1B4776]" : "text-white"}`}>
                    {aiActive ? "İrsaliye okunuyor…" : "Fotoğraf veya PDF ile Tara"}
                  </div>
                  <div className={`text-xs mt-1 ${aiActive ? "text-slate-600" : "text-slate-300"}`}>
                    JPG · PNG · PDF · Düzenleyen + Düzenlenen firma, taşıyıcı ve malzeme bilgileri otomatik doldurulur
                  </div>
                </>
              )}
            </div>
            <input
              type="file" accept="image/jpeg,image/png,image/webp,application/pdf,.pdf"
              className="hidden"
              data-testid="ai-scan-input"
              onChange={onAiScan}
              disabled={aiActive || uploading || !online}
            />
          </label>

          {/* Confidence rozetleri */}
          {Object.keys(confidence).length > 0 && !aiActive && (
            <div className="mt-4 pt-4 border-t border-slate-200 space-y-1.5">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">AI Güven Değerleri</div>
              {[
                ["issuer", "Düzenleyen Firma"],
                ["recipient", "Düzenlenen Firma"],
                ["carrier", "Taşıyıcı"],
                ["delivery_date", "Tarih"],
                ["delivery_note_no", "İrsaliye No"],
                ["material_name", "Malzeme"],
                ["quantity", "Miktar"],
                ["unit", "Birim"],
                ["unit_price", "Birim Fiyat"],
              ].map(([k, label]) => {
                const c = confidence[k];
                if (!c) return null;
                const color = c === "high" ? "text-emerald-700 bg-emerald-50" :
                              c === "medium" ? "text-amber-800 bg-amber-50" :
                              "text-red-700 bg-red-50";
                const Icon = c === "high" ? Check : Warning;
                return (
                  <div key={k} className="flex items-center justify-between text-xs">
                    <span className="text-slate-600">{label}</span>
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 font-semibold ${color}`}>
                      <Icon size={11} weight="bold" />
                      {c === "high" ? "Yüksek" : c === "medium" ? "Orta" : "Düşük"}
                    </span>
                  </div>
                );
              })}
              <div className="text-[10px] text-slate-500 pt-2">
                Kırmızı/sarı rozetli alanları mutlaka kontrol edin.
              </div>
            </div>
          )}

          {/* Manuel yükleme */}
          <div className="mt-4 pt-4 border-t border-slate-200">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">
              Ya da sadece belgeyi ekle
            </div>
            <label className="block cursor-pointer" data-testid="upload-label">
              <div className="border border-slate-300 hover:border-[#1B4776] transition-colors bg-slate-50 p-3 text-center text-xs text-slate-700 flex items-center justify-center gap-2">
                <ImageSquare size={14} weight="duotone" className="text-[#1B4776]" />
                Belge Yükle (AI kullanmadan)
              </div>
              <input
                type="file" accept="image/*,.pdf"
                className="hidden"
                data-testid="upload-input"
                onChange={onUpload}
                disabled={uploading || aiActive}
              />
            </label>
            {uploading && <div className="text-xs text-slate-500 mt-2 flex items-center gap-2"><UploadSimple size={14} /> Yükleniyor…</div>}
            {form.photo_path && !uploading && !aiActive && (
              <div className="text-xs text-emerald-700 mt-2 truncate">✓ Belge yüklendi</div>
            )}
          </div>
        </Card>
      </form>

      {/* Firma tespit onay dialogu (issuer VE/VEYA recipient için sıralı) */}
      <AlertDialog open={!!currentPending && !supplierSheetOpen}>
        <AlertDialogContent
          className="rounded-none sm:rounded-sm"
          onEscapeKeyDown={() => setCurrentPending(null)}
          onPointerDownOutside={() => setCurrentPending(null)}
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading font-bold">
              {currentPending?.role === "recipient"
                ? "Düzenlenen firma tespit edildi"
                : "Düzenleyen firma tespit edildi"}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                Sistem <strong>{currentPending?.info?.title || currentPending?.info?.name || "bir firma"}</strong> isimli firmayı tespit etti ancak bu firma sistemde kayıtlı değil.
              </span>
              <span className="block text-slate-600">Bu firma sisteme kaydedilsin mi?</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-no"
              onClick={() => setCurrentPending(null)}
              className="rounded-none sm:rounded-sm">
              Hayır, atla
            </AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-yes"
              onClick={() => setSupplierSheetOpen(true)}
              className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859]">
              Evet, kaydet
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AI ile ön dolu firma kaydı paneli */}
      <SupplierQuickCreate
        open={supplierSheetOpen}
        onOpenChange={(v) => {
          if (!v) {
            // Sheet kapandığında bu firma iş bitti — sonraki queue elemanına geç
            setSupplierSheetOpen(false);
            setCurrentPending(null);
          } else {
            setSupplierSheetOpen(true);
          }
        }}
        pendingInfo={currentPending?.info}
        pendingRole={currentPending?.role}
        saving={supplierSaving}
        saveFn={saveSupplierFromAi}
      />
    </Shell>
  );
}
